import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import { getBrukerDisplayName } from '@klp/user/utils';
import { useCurrentUser } from '@klp/auth';
import type { Aktivitet } from '@klp/formgenerator/interfaces';
import {
    findCamundaProperty,
    findCamundaPropertyValue,
} from '@klp/formgenerator/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type TildelKommentar = {
    activityId: string;
    user: string;
    timestamp: string;
    comment: string;
};

const MAX_CAMUNDA_COMMENT_LENGTH = 4000;

const shortId = (id: string | undefined | null): string =>
    id?.slice(0, 8) ?? '';

const parse = (raw: string): Array<TildelKommentar> => {
    if (!raw) return [];
    return raw
        .split('\n')
        .map((line) => {
            const [activityId, user, timestamp, ...rest] = line.split('|');
            return {
                activityId: shortId(activityId.trim()),
                user,
                timestamp,
                comment: rest.join('|'),
            };
        })
        .filter((c) => c.activityId && c.user && c.timestamp);
};

const serialize = (comments: Array<TildelKommentar>): string =>
    comments
        .map(
            ({ activityId, user, timestamp, comment }) =>
                `${activityId}|${user}|${timestamp}|${comment}`,
        )
        .join('\n');

export const useTildelKommentar = (activity: Aktivitet | undefined) => {
    const { t } = useTranslation();
    const { currentUser } = useCurrentUser();
    const userName =
        getBrukerDisplayName(currentUser?.fulltnavn, currentUser?.epost) ||
        'Unknown user';
    const activityId = shortId(activity?.id);

    const isTildelKommentarerFieldInTheForm = useMemo(
        () =>
            Boolean(
                activity &&
                findCamundaProperty(
                    CamundaFields.TILDEL_KOMMENTARER,
                    activity?.formFields ?? [],
                ),
            ),
        [activity],
    );

    const rawValue = useMemo(
        () =>
            (activity &&
                findCamundaPropertyValue(
                    CamundaFields.TILDEL_KOMMENTARER,
                    activity.formFields ?? [],
                )) ||
            '',
        [activity],
    );

    const allComments = useMemo(() => parse(rawValue), [rawValue]);

    const attachComment = (newComment: string): string => {
        if (!newComment.trim()) return serialize(allComments);

        const newEntry: TildelKommentar = {
            activityId,
            user: userName,
            timestamp: new Date().toISOString(),
            comment: newComment.trim(),
        };

        const updated = serialize([...allComments, newEntry]);

        if (updated.length > MAX_CAMUNDA_COMMENT_LENGTH) {
            throw new Error(
                t(
                    `prosjekt.${CamundaFields.TILDEL_KOMMENTARER}.field.error.commentTooLong`,
                ),
            );
        }

        return updated;
    };

    const maxCommentLength = useMemo(() => {
        const newEntry = {
            activityId,
            user: userName,
            timestamp: new Date().toISOString(),
            comment: '',
        };

        const updated = serialize([...allComments, newEntry]);
        return MAX_CAMUNDA_COMMENT_LENGTH - updated.length;
    }, [allComments, activityId, userName]);

    return {
        allComments,
        isTildelKommentarerFieldInTheForm,
        attachComment,
        maxCommentLength,
    };
};
